import type { Product, Variant } from '../../types'

/** Stable absolute site origin (no trailing slash). */
const SITE_ORIGIN = 'https://store.globalcontrol.group'
const DEFAULT_CURRENCY = 'EUR'

/**
 * Compute the canonical URL for a product detail view.
 * - When no variants exist, canonical is `/product/{slug}`.
 * - When a default variant exists, canonical is still `/product/{slug}` (the
 *   variant URL `/product/{slug}/{variantSlug}` is treated as a specific
 *   sub-page with a self-referential canonical).
 */
export function canonicalPath(lang: string, productSlug: string, variantSlug?: string): string {
  const l = encodeURIComponent(lang)
  const p = encodeURIComponent(productSlug)
  if (variantSlug) {
    return `/${l}/product/${p}/${encodeURIComponent(variantSlug)}`
  }
  return `/${l}/product/${p}`
}

export function canonicalUrl(lang: string, productSlug: string, variantSlug?: string): string {
  return `${SITE_ORIGIN}${canonicalPath(lang, productSlug, variantSlug)}`
}

const BRAND = 'Global Control Group'

/** Max length of the title base, before the " | Global Control Group" suffix. */
const MAX_TITLE_BASE = 65

/**
 * Build the <title> for the page (WEB-394). Order of preference:
 *  1. an explicit per-variant SEO title;
 *  2. a clean, concise catalog display title (usually already "{MPN} {type}");
 *  3. a composed "{MPN} – {type}" string.
 *
 * This keeps titles short (~50–60 chars, what Google shows) and never dumps the
 * raw "Material number: … Description: …" catalog string or the full long
 * description into the tab/SERP. The title is intentionally language-neutral:
 * a B&R model number plus a technical product type reads the same in NL and EN.
 */
export function pageTitle(product: Product, variant: Variant | null): string {
  if (variant?.seo_title) return variant.seo_title
  const mpn =
    variant?.manufacturer_part_number || product.manufacturer_part_number ||
    variant?.normalized_mpn || product.normalized_mpn
  const base = titleBase(product, variant, mpn)
  if (variant?.variant_label) {
    return `${base}, ${variant.variant_label} | ${BRAND}`
  }
  return `${base} | ${BRAND}`
}

/** A real catalog title when there is a usable one, else a composed "{MPN} – {type}". */
function titleBase(product: Product, variant: Variant | null, mpn: string): string {
  const raw = (variant?.display_title || product.display_title || '').trim()
  // A short, real catalog title is the best base — it is usually MPN-led
  // already. Skip it when it is missing, just the bare MPN, a raw
  // "Material number: …" dump, or too long to be a sensible SEO title.
  if (raw && raw !== mpn && !isRawDump(raw) && raw.length <= MAX_TITLE_BASE) {
    return raw
  }
  const type = productType(product)
  return type ? `${mpn} – ${type}` : mpn
}

/** The raw catalog concatenation that must never surface in a title. */
function isRawDump(s: string): boolean {
  return /material\s*number\s*:|description\s*:/i.test(s)
}

/** A concise product type: subcategory, else a short description clause, else category. */
function productType(product: Product): string {
  const sub = product.subcategory?.trim()
  if (sub) return sub
  const fromDesc = shortClause(product.short_description)
  if (fromDesc) return fromDesc
  const cat = product.category?.trim()
  if (cat && cat.toLowerCase() !== 'uncategorized') return cat
  return ''
}

/** First clause of a description, with "Material number: … Description:" boilerplate stripped. */
function shortClause(desc: string | null | undefined): string {
  let s = (desc ?? '').trim()
  if (s === '') return ''
  const m = s.match(/description\s*:\s*(.+)$/i)
  if (m) s = m[1].trim()
  s = s.split(/[,.;]/)[0].trim()
  if (s === '') return ''
  return s.length > 48 ? s.slice(0, 47).trimEnd() + '…' : s
}

/**
 * Meta description. Prefer per-variant SEO description, fall back to short
 * product description, then to a safe short sentence.
 */
export function metaDescription(product: Product, variant: Variant | null): string {
  if (variant?.seo_description) return variant.seo_description
  const short = product.short_description?.trim()
  if (short) return truncate(short, 155)
  const long = product.long_description?.trim()
  if (long) return truncate(long, 155)
  const title = variant?.display_title || product.display_title || product.normalized_mpn
  return `${title}. Koop online bij Global Control Group, de B&R distributeur in Nederland.`
}

/**
 * Return every variant URL for <link rel="alternate"> tags so crawlers can see
 * the sibling relationships (ARCHITECTURE.md §4). Frozen in DEC-016.
 */
export function variantAlternates(
  lang: string,
  productSlug: string,
  variants: readonly Variant[],
): Array<{ url: string; variant: Variant }> {
  return variants.map((v) => ({
    url: canonicalUrl(lang, productSlug, v.slug),
    variant: v,
  }))
}

export interface ProductJsonLdOffer {
  '@type': 'Offer'
  url: string
  priceCurrency: string
  price?: string
  availability: string
  itemCondition: string
}

export interface ProductJsonLd {
  '@context': 'https://schema.org'
  '@type': 'Product'
  name: string
  description: string
  sku: string
  mpn: string
  brand: { '@type': 'Brand'; name: string }
  image?: string[]
  url: string
  offers: ProductJsonLdOffer
  /** Per DEC-016: variant pages back-reference their parent ProductGroup. */
  isVariantOf?: {
    '@type': 'ProductGroup'
    name: string
    url: string
    productGroupID: string
  }
}

function schemaStockStatus(stock: string | null | undefined): string {
  switch (stock) {
    case 'in_stock':
    case 'low_stock':
      return 'https://schema.org/InStock'
    case 'out_of_stock':
      return 'https://schema.org/OutOfStock'
    case 'on_request':
    case 'on_order':
      return 'https://schema.org/PreOrder'
    default:
      return 'https://schema.org/BackOrder'
  }
}

/**
 * Build a Product + Offer JSON-LD block for the current variant URL.
 * When no variant exists, falls back to product-level price/stock.
 *
 * When `options.isCanonicalPage` is `true`, the offers URL uses the canonical
 * `/product/{slug}` path (no variant segment). Default: include the variant
 * slug so the URL is self-referential for the variant page.
 */
export function productJsonLd(
  lang: string,
  product: Product,
  variant: Variant | null,
  options: { isCanonicalPage?: boolean } = {},
): ProductJsonLd {
  const price = variant?.effective_price ?? product.effective_price
  const stock = variant?.stock_status ?? product.stock_status
  const image = variant?.primary_image_url || product.primary_image_url
  const urlSlug = options.isCanonicalPage ? undefined : variant?.slug
  const url = canonicalUrl(lang, product.slug, urlSlug)

  const offer: ProductJsonLdOffer = {
    '@type': 'Offer',
    url,
    priceCurrency: DEFAULT_CURRENCY,
    availability: schemaStockStatus(stock),
    itemCondition: 'https://schema.org/NewCondition',
  }
  if (price != null && price !== '') {
    // JSON-LD price must be a plain decimal string — two decimals is enough
    // for display purposes; store precision is handled server-side.
    offer.price = Number(price).toFixed(2)
  }

  const json: ProductJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: variant?.display_title || product.display_title || product.normalized_mpn,
    description:
      product.short_description?.trim() ||
      product.long_description?.trim() ||
      `${product.display_title} from ${product.brand || product.manufacturer || 'B&R'}.`,
    sku: variant?.normalized_mpn || product.normalized_mpn,
    mpn: variant?.manufacturer_part_number || product.manufacturer_part_number,
    brand: { '@type': 'Brand', name: product.brand || product.manufacturer || 'B&R' },
    ...(image ? { image: [image] } : {}),
    url,
    offers: offer,
  }

  // Variant pages reference their parent ProductGroup so search engines
  // understand the variant relationship and don't treat siblings as
  // duplicate products. See [[variant-model]] memory + DEC-016.
  if (variant && variant.normalized_mpn !== product.normalized_mpn) {
    json.isVariantOf = {
      '@type': 'ProductGroup',
      name: product.display_title || product.normalized_mpn,
      url: canonicalUrl(lang, product.slug),
      productGroupID: product.normalized_mpn,
    }
  }

  return json
}

export interface BreadcrumbListJsonLd {
  '@context': 'https://schema.org'
  '@type': 'BreadcrumbList'
  itemListElement: Array<{
    '@type': 'ListItem'
    position: number
    name: string
    item: string
  }>
}

export function breadcrumbJsonLd(
  lang: string,
  product: Product,
  variant: Variant | null,
): BreadcrumbListJsonLd {
  const crumbs: Array<{ name: string; path: string }> = [
    { name: 'Home', path: `/${encodeURIComponent(lang)}` },
    { name: 'Producten', path: `/${encodeURIComponent(lang)}/products` },
  ]
  if (product.category) {
    crumbs.push({
      name: product.category,
      path: `/${encodeURIComponent(lang)}/products?category=${encodeURIComponent(product.category)}`,
    })
  }
  crumbs.push({
    name: variant?.display_title || product.display_title || product.normalized_mpn,
    path: canonicalPath(lang, product.slug, variant?.slug),
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: c.name,
      item: `${SITE_ORIGIN}${c.path}`,
    })),
  }
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s
  return s.slice(0, max - 1).trimEnd() + '\u2026'
}

/**
 * Exported so tests can assert against the same constant.
 */
export const SITE_ORIGIN_FOR_TESTS = SITE_ORIGIN
